"use client";

import type React from "react";
import { useCallback, useState } from "react";
import Link from "next/link";
import { AnimatePresence, motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowUpRight, Check, ChevronDown, RefreshCw } from "@/components/icons";
import {
  FakeCursor,
  useFakeCursor,
  FAKE_CURSOR_MOVE_MS,
  CardWave,
  waveSeed,
  type FakeCursorStep,
  type SetCursorTarget,
} from "@/components/ui";
import { STATUS_URL } from "@/lib/constants";
import type { UptimeStatus } from "@/hooks/use-uptime";
import { cn } from "@/lib/utils";

/* Big-card visuals for the Why Proxidize grid — token-based skeletons that read
   as product screenshots in both themes (bg-popover frame + foreground-alpha
   fills), with brand-green accents. Fixed content so heights stay stable. */

const GREEN_TINT = "color-mix(in srgb, var(--brand-green) 12%, transparent)";

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};
const staticVariants = { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } };

/* Identical treatment to the Use Cases bento — rounded, bordered, a wave across
   the head that dissolves into the card's own surface. The backdrop itself now
   lives in ui/card-wave (CardWave + waveSeed): it was written out by hand here
   and again in Use Cases, and the product pages' flagship cards are a third
   caller, so the mask stops and the fallback gradient had to stop being kept in
   step by hand. GradientWave is on its default ramp, so all three match; only
   the per-card seed differs. */

/** Shared card shell: rounded, bordered, wave-backed, content above. */
const CARD_SHELL =
  "bg-card border-border relative isolate flex flex-col overflow-hidden rounded-2xl border";

interface BigFeatureCardProps {
  title: string;
  description: string;
  children: React.ReactNode;
  reduced: boolean | null;
}

export function BigFeatureCard({ title, description, children, reduced }: BigFeatureCardProps) {
  return (
    <article className={cn(CARD_SHELL, "p-6 sm:p-8")}>
      <CardWave seed={waveSeed(title)} />
      {/* Only the content animates in; the tile keeps its own surface. */}
      <motion.div
        variants={reduced ? staticVariants : cardVariants}
        className="relative z-10 flex flex-1 flex-col"
      >
        <h3 className="text-foreground text-[19px] font-semibold tracking-[-0.01em]">{title}</h3>
        <p className="text-muted-foreground mt-1.5 max-w-md text-sm leading-relaxed">
          {description}
        </p>
        <div className="mt-6 flex flex-1 items-end">{children}</div>
      </motion.div>
    </article>
  );
}

interface SmallFeatureCardProps {
  Visual: React.ComponentType;
  title: string;
  description: string;
  reduced: boolean | null;
}

/* No WaveBackdrop here on purpose: only the two flagship cards carry the
   gradient, so the four supporting cards read as plain surfaces beneath them. */
export function SmallFeatureCard({ Visual, title, description, reduced }: SmallFeatureCardProps) {
  return (
    <article className={cn(CARD_SHELL, "p-6")}>
      <motion.div
        variants={reduced ? staticVariants : cardVariants}
        className="relative z-10 flex flex-col"
      >
        <div className="mb-5">
          <Visual />
        </div>
        <h3 className="text-foreground text-[15px] font-semibold tracking-[-0.01em]">{title}</h3>
        <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">{description}</p>
      </motion.div>
    </article>
  );
}

/* Field fills that sit one step above the popover surface — a hair of the
   foreground color, so they read as inset inputs in both themes. */
const FIELD_BG = "color-mix(in srgb, var(--foreground) 3%, transparent)";

const DASH_COUNTRY_DEFAULT = "Canada";
const DASH_COUNTRY_PICKED = "United States";
const DASH_COUNTRIES = ["Canada", "United States", "Germany", "France"];

/* The access-point config row from the residential Per-GB page. */
const DASH_DROPDOWNS = [
  { id: "country", label: "Country", value: DASH_COUNTRY_DEFAULT },
  { id: "type", label: "Type", value: "HTTP Proxy" },
  { id: "city", label: "City", value: "Random City" },
  { id: "isp", label: "ISP", value: "Random Network" },
  { id: "stick", label: "Stickiness", value: "Sticky IP" },
  { id: "host", label: "Hostname", value: "DNS Hostname" },
];

interface DashChipProps {
  label: string;
  value: string;
  /** Rotates the chevron while the fake dropdown menu is open. */
  open?: boolean;
  /** Registers the chip as a fake-cursor click target. */
  chipRef?: (el: HTMLDivElement | null) => void;
}

/** A read-only labelled dropdown pill (label over value + chevron). */
function DashChip({ label, value, open = false, chipRef }: DashChipProps) {
  return (
    <div ref={chipRef} className="border-border rounded-md border px-2 py-1">
      <div className="text-muted-foreground text-[8px] tracking-wide uppercase">{label}</div>
      <div className="mt-0.5 flex items-center justify-between gap-1">
        <span className="text-foreground truncate text-[10px]">{value}</span>
        <ChevronDown
          size={9}
          className={cn(
            "text-muted-foreground shrink-0 transition-transform duration-200",
            open && "rotate-180"
          )}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

interface DashFieldProps {
  label: string;
  value: string;
  refresh?: boolean;
  /** Registers the refresh icon as a fake-cursor click target. */
  refreshRef?: (el: HTMLSpanElement | null) => void;
  /** Bump to spin the refresh icon one full turn (fake credential rotation). */
  spins?: number;
}

/** A read-only detail field (label over an inset value input). */
function DashField({ label, value, refresh = false, refreshRef, spins = 0 }: DashFieldProps) {
  return (
    <div>
      <div className="text-muted-foreground mb-0.5 text-[9px]">{label}</div>
      <div
        className="border-border flex items-center justify-between gap-2 rounded-md border px-2 py-1.5"
        style={{ backgroundColor: FIELD_BG }}
      >
        <span className="text-foreground truncate font-mono text-[10px]">{value}</span>
        {refresh && (
          <motion.span
            key={spins}
            ref={refreshRef}
            className="flex shrink-0"
            initial={{ rotate: 0 }}
            animate={{ rotate: spins > 0 ? 360 : 0 }}
            transition={{ duration: 0.7, ease: "easeInOut" }}
          >
            <RefreshCw size={10} className="text-muted-foreground" aria-hidden="true" />
          </motion.span>
        )}
      </div>
    </div>
  );
}

const DASH_TABS = ["Access Point", "Session Builder", "Buy"];

/* Session Builder config row (Per-GB · Session Builder tab). */
const SESSION_CONFIG = [
  { id: "s-type", label: "Type", value: "HTTP Proxy" },
  { id: "s-country", label: "Country", value: "Random Country" },
  { id: "s-city", label: "City", value: "Random City" },
  { id: "s-isp", label: "ISP", value: "Random Network" },
  { id: "s-stick", label: "Stickiness", value: "Randomize IP" },
  { id: "s-count", label: "Count", value: "10" },
];

/* Buy tab "What's Included" perks — mirrors the residential per-GB card highlights. */
const BUY_INCLUDED = [
  "Unused data rolls over",
  "City & ISP targeting",
  "HTTP, HTTPS & SOCKS5",
  "Real-time dashboard & API",
];

/* Solid brand-green action button — readable text on green in both themes. */
const PRIMARY_BTN: React.CSSProperties = {
  backgroundColor: "var(--brand-green)",
  color: "var(--background)",
};

interface AccessPointPanelProps {
  country: string;
  countryOpen: boolean;
  pwSpins: number;
  setTarget: SetCursorTarget;
}

/** Access Point panel — config dropdowns, proxy details, and a cURL example.
    The Country chip carries a real menu the fake cursor opens and picks from. */
function AccessPointPanel({ country, countryOpen, pwSpins, setTarget }: AccessPointPanelProps) {
  return (
    <div className="space-y-2.5">
      <div className="grid grid-cols-3 gap-1.5">
        {DASH_DROPDOWNS.map((d) =>
          d.id === "country" ? (
            <div key={d.id} className="relative">
              <DashChip
                label={d.label}
                value={country}
                open={countryOpen}
                chipRef={setTarget("ap-country")}
              />
              {/* The menu the cursor opens — the option it will pick carries the
                  "ap-country-option" target; the current country shows a check. */}
              <AnimatePresence>
                {countryOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.98 }}
                    transition={{ duration: 0.18 }}
                    className="border-border bg-popover absolute inset-x-0 top-full z-10 mt-1 rounded-md border py-0.5 shadow-lg"
                  >
                    {DASH_COUNTRIES.map((c) => (
                      <div
                        key={c}
                        ref={c === DASH_COUNTRY_PICKED ? setTarget("ap-country-option") : undefined}
                        className={cn(
                          "flex items-center justify-between px-2 py-1 text-[10px]",
                          c === country ? "text-foreground font-medium" : "text-muted-foreground"
                        )}
                      >
                        {c}
                        {c === country && (
                          <Check
                            size={9}
                            style={{ color: "var(--brand-green)" }}
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <DashChip key={d.id} label={d.label} value={d.value} />
          )
        )}
      </div>
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <DashField label="Proxy Host" value="pg.proxi.es" />
          <DashField label="Proxy Port" value="20000" />
        </div>
        <DashField
          label="Proxy Password"
          value="••••••••••••••••"
          refresh
          refreshRef={setTarget("ap-refresh")}
          spins={pwSpins}
        />
      </div>
      <div
        className="border-border overflow-hidden rounded-md border px-2.5 py-2"
        style={{ backgroundColor: FIELD_BG }}
      >
        <p className="text-muted-foreground truncate font-mono text-[10px]">
          <span style={{ color: "var(--brand-green)" }}>curl</span> -x
          &quot;http://user:pass@pg.proxi.es:20000&quot; https://ipv4.icanhazip.com
        </p>
      </div>
    </div>
  );
}

/* Rows that appear in the Session Builder list once the cursor "adds" them. */
const SESSION_ROWS = [
  { id: "sr-1", host: "pg.proxi.es:20101", meta: "HTTP · Random IP" },
  { id: "sr-2", host: "pg.proxi.es:20102", meta: "HTTP · Random IP" },
];

interface SessionBuilderPanelProps {
  added: boolean;
  setTarget: SetCursorTarget;
}

/** Session Builder panel — a configure row, an add button, and the proxy list
    (empty until the fake cursor clicks "Add"). */
function SessionBuilderPanel({ added, setTarget }: SessionBuilderPanelProps) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-baseline gap-2">
        <span className="text-foreground text-[9px] font-semibold tracking-wide uppercase">
          Configure
        </span>
        <span className="text-muted-foreground text-[9px]">Set values, then add.</span>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {SESSION_CONFIG.map((d) => (
          <DashChip key={d.id} label={d.label} value={d.value} />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground flex items-center gap-1 text-[10px]">
          <RefreshCw size={9} aria-hidden="true" /> Reset fields
        </span>
        <span
          ref={setTarget("sb-add")}
          className="rounded-md px-2.5 py-1 text-[10px] font-semibold"
          style={PRIMARY_BTN}
        >
          + Add 10 proxies
        </span>
      </div>
      {added ? (
        <div className="space-y-1.5">
          {SESSION_ROWS.map((row, i) => (
            <motion.div
              key={row.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: i * 0.08 }}
              className="border-border flex items-center justify-between gap-2 rounded-md border px-2 py-1"
              style={{ backgroundColor: FIELD_BG }}
            >
              <span className="text-foreground truncate font-mono text-[10px]">{row.host}</span>
              <span className="text-muted-foreground shrink-0 text-[9px]">{row.meta}</span>
            </motion.div>
          ))}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25, delay: 0.2 }}
            className="text-muted-foreground text-center text-[9px]"
          >
            + 8 more added
          </motion.p>
        </div>
      ) : (
        <div className="border-border rounded-md border border-dashed py-4 text-center">
          <p className="text-muted-foreground text-[10px]">Your proxy list will appear here</p>
        </div>
      )}
    </div>
  );
}

interface BuyPanelProps {
  gb: number;
  setTarget: SetCursorTarget;
}

/* The mock slider spans 0–1000 GB, so position % = gb / 10. The thumb and fill
   transition at the cursor's glide pace so a drag reads as one motion. */
const SLIDER_MOVE = `${FAKE_CURSOR_MOVE_MS}ms cubic-bezier(0.25, 0.8, 0.3, 1)`;

/** Buy panel — the plan slider (cursor-draggable), price, and included perks. */
function BuyPanel({ gb, setTarget }: BuyPanelProps) {
  const pct = gb / 10;
  return (
    <div className="space-y-3">
      <div>
        <p className="text-foreground text-[13px] font-semibold">Residential Proxies Per GB</p>
        <p className="text-muted-foreground text-[10px] leading-relaxed">
          Premium residential IPs, ethically sourced from opt-in devices.
        </p>
      </div>
      <div className="space-y-2">
        <div className="flex items-baseline gap-1">
          <span className="text-foreground text-[22px] leading-none font-bold tracking-tight">
            {gb}
          </span>
          <span className="text-muted-foreground text-[10px]">GB</span>
        </div>
        <div ref={setTarget("buy-track")} className="bg-foreground/10 relative h-1 rounded-full">
          <div
            className="bg-brand-green absolute inset-y-0 left-0 rounded-full"
            style={{ width: `${pct}%`, transition: `width ${SLIDER_MOVE}` }}
          />
          <span
            className="bg-brand-green absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              left: `${pct}%`,
              boxShadow: "0 0 0 3px var(--popover)",
              transition: `left ${SLIDER_MOVE}`,
            }}
          />
        </div>
      </div>
      <div className="border-border flex items-center justify-between border-t pt-2.5">
        <div className="flex items-baseline gap-1">
          <span className="text-brand-green text-[17px] font-bold tracking-tight">${gb}.00</span>
          <span className="text-muted-foreground text-[10px]">/mo</span>
        </div>
        <span
          ref={setTarget("buy-cta")}
          className="rounded-md px-3 py-1 text-[10px] font-semibold"
          style={PRIMARY_BTN}
        >
          Buy Now
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
        {BUY_INCLUDED.map((item) => (
          <span key={item} className="text-muted-foreground flex items-center gap-1 text-[9px]">
            <Check size={10} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

/* The cursor's demo session: open the Country dropdown and pick United States,
   rotate the password, build a session, then drag the Buy slider to 750 GB and
   check out. */
const DASH_CURSOR_SCRIPT: FakeCursorStep[] = [
  { id: "open-country", target: "ap-country", hold: 1500 },
  { id: "pick-country", target: "ap-country-option", hold: 650 },
  { id: "rotate-password", target: "ap-refresh", hold: 1300 },
  { id: "open-session-builder", target: "tab-1", hold: 1500 },
  { id: "add-proxies", target: "sb-add", hold: 1700 },
  { id: "open-buy", target: "tab-2", hold: 1700 },
  { id: "grab-slider", target: "buy-track", at: 0.5, hold: 1500, holdPress: true },
  { id: "drag-slider", target: "buy-track", at: 0.75, hold: 400, arrive: "release" },
  { id: "buy-now", target: "buy-cta", hold: 1300 },
  { id: "back-to-start", target: "tab-0", hold: 1700 },
];

/** Dashboard mock — a compact recreation of the residential Per-GB page. A fake
    mouse cursor works through a scripted session: it reconfigures the access
    point, rotates the password, switches tabs, adds session proxies, drags the
    Buy slider, and checks out — each click a press + ripple, with a sliding tab
    underline and cross-fading panels. Holds on the first tab with no cursor
    under reduced motion. Token-based for dark and light. Decorative. */
export function DashboardMock() {
  const reduced = useReducedMotion();
  const [tab, setTab] = useState(0);

  // Scene state the cursor's actions mutate; reset when the loop restarts.
  const [country, setCountry] = useState(DASH_COUNTRY_DEFAULT);
  const [countryOpen, setCountryOpen] = useState(false);
  const [pwSpins, setPwSpins] = useState(0);
  const [sessionAdded, setSessionAdded] = useState(false);
  const [gb, setGb] = useState(500);

  // The slider thumb starts moving with the cursor, not after it arrives.
  const handleMove = useCallback((s: FakeCursorStep) => {
    if (s.id === "drag-slider") setGb(750);
  }, []);

  const handleClick = useCallback((s: FakeCursorStep) => {
    switch (s.id) {
      case "open-country":
        setCountryOpen(true);
        break;
      case "pick-country":
        setCountryOpen(false);
        setCountry(DASH_COUNTRY_PICKED);
        break;
      case "rotate-password":
        setPwSpins((v) => v + 1);
        break;
      case "open-session-builder":
        setTab(1);
        break;
      case "add-proxies":
        setSessionAdded(true);
        break;
      case "open-buy":
        setTab(2);
        break;
      case "back-to-start":
        setTab(0);
        setCountry(DASH_COUNTRY_DEFAULT);
        setCountryOpen(false);
        setPwSpins(0);
        setSessionAdded(false);
        setGb(500);
        break;
      default:
        break;
    }
  }, []);

  const { frameRef, setTarget, cursor, clicking } = useFakeCursor({
    script: DASH_CURSOR_SCRIPT,
    parkAt: "tab-0",
    onMove: handleMove,
    onClick: handleClick,
  });

  return (
    <div
      ref={frameRef}
      className="border-border bg-popover relative w-full overflow-hidden rounded-xl border"
      aria-hidden="true"
    >
      <FakeCursor cursor={cursor} clicking={clicking} />
      {/* Browser chrome + address bar */}
      <div className="border-border flex items-center gap-2 border-b px-3 py-2">
        <span className="flex shrink-0 items-center gap-1.5">
          <span className="bg-foreground/15 h-2 w-2 rounded-full" />
          <span className="bg-foreground/15 h-2 w-2 rounded-full" />
          <span className="bg-foreground/15 h-2 w-2 rounded-full" />
        </span>
        <span className="bg-foreground/5 text-muted-foreground ml-1 min-w-0 flex-1 truncate rounded-md px-2 py-1 text-[10px]">
          app.proxidize.com/proxies/residential/per-gb
        </span>
      </div>

      <div className="p-3">
        {/* Tabs (auto-cycling) + persistent usage */}
        <div className="border-border mb-3 flex items-center justify-between border-b">
          <div className="flex items-center gap-3 text-[11px]">
            {DASH_TABS.map((t, i) => {
              const active = i === tab;
              return (
                <span
                  key={t}
                  ref={setTarget(`tab-${i}`)}
                  className={cn(
                    "relative pb-2",
                    active ? "text-foreground font-medium" : "text-muted-foreground"
                  )}
                >
                  {t}
                  {active && (
                    <motion.span
                      layoutId={reduced ? undefined : "dash-tab-underline"}
                      className="bg-brand-green absolute bottom-0 left-0 h-0.5 w-full rounded-full"
                      transition={
                        reduced ? { duration: 0 } : { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
                      }
                    />
                  )}
                </span>
              );
            })}
          </div>
          <div className="flex items-center gap-2 pb-2">
            <span className="text-muted-foreground hidden items-center gap-1.5 text-[10px] sm:flex">
              <span className="relative flex h-1.5 w-1.5">
                {!reduced && (
                  <span className="bg-brand-green absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" />
                )}
                <span className="bg-brand-green relative inline-flex h-1.5 w-1.5 rounded-full" />
              </span>
              299.93 GB left
            </span>
            <span
              className="text-brand-green border-brand-green/30 rounded-md border px-2 py-1 text-[10px] font-medium"
              style={{ backgroundColor: GREEN_TINT }}
            >
              + Buy More
            </span>
          </div>
        </div>

        {/* Cycling panel — fixed height so the card never resizes between tabs */}
        <div className="relative h-52 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={reduced ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduced ? undefined : { opacity: 0, y: -6 }}
              transition={{ duration: 0.3 }}
            >
              {tab === 0 && (
                <AccessPointPanel
                  country={country}
                  countryOpen={countryOpen}
                  pwSpins={pwSpins}
                  setTarget={setTarget}
                />
              )}
              {tab === 1 && <SessionBuilderPanel added={sessionAdded} setTarget={setTarget} />}
              {tab === 2 && <BuyPanel gb={gb} setTarget={setTarget} />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/* 90 uptime bars, all green except two brief dips — a status-page history. */
const UPTIME_BARS = Array.from({ length: 90 }, (_, i) => ({
  id: `u${i}`,
  dip: i === 41 || i === 68,
}));

/* Map the live BetterStack status to a label + dot colour. */
const UPTIME_STATUS: Record<UptimeStatus, { label: string; color: string }> = {
  operational: { label: "All systems operational", color: "var(--brand-green)" },
  degraded: { label: "Degraded performance", color: "var(--amber-accent)" },
  maintenance: { label: "Scheduled maintenance", color: "var(--amber-accent)" },
  downtime: { label: "Partial outage", color: "var(--destructive)" },
};

interface UptimeMockProps {
  uptime: number;
  status?: UptimeStatus;
}

export function UptimeMock({ uptime, status = "operational" }: UptimeMockProps) {
  const reduced = useReducedMotion();
  const meta = UPTIME_STATUS[status] ?? UPTIME_STATUS.operational;
  return (
    <div className="border-border bg-popover w-full rounded-xl border p-4">
      <div className="flex items-center justify-between">
        <span
          className="flex items-center gap-1.5 text-[12px] font-medium"
          style={{ color: meta.color }}
        >
          <span className="relative flex h-2 w-2" aria-hidden="true">
            {!reduced && (
              <span
                className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
                style={{ backgroundColor: meta.color }}
              />
            )}
            <span
              className="relative inline-flex h-2 w-2 rounded-full"
              style={{ backgroundColor: meta.color }}
            />
          </span>
          {meta.label}
        </span>
        <span className="text-muted-foreground text-[11px]">Last 90 days</span>
      </div>
      <motion.div
        className="mt-4 flex h-16 items-end gap-0.5"
        aria-hidden="true"
        initial={reduced ? false : "hidden"}
        whileInView={reduced ? undefined : "visible"}
        viewport={scrollRevealViewport}
        variants={{ visible: { transition: { staggerChildren: 0.008 } } }}
      >
        {UPTIME_BARS.map((bar) => (
          <motion.div
            key={bar.id}
            className={cn("flex-1 origin-bottom rounded-sm", bar.dip ? "h-2/5" : "h-full")}
            style={{
              backgroundColor: bar.dip
                ? "var(--amber-accent)"
                : "color-mix(in srgb, var(--brand-green) 55%, transparent)",
            }}
            variants={reduced ? undefined : { hidden: { scaleY: 0.12 }, visible: { scaleY: 1 } }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
        ))}
      </motion.div>
      <div className="border-border mt-4 flex items-center justify-between border-t pt-4">
        <div>
          <div className="text-foreground text-2xl font-semibold tracking-tight">
            {uptime.toFixed(3)}%
          </div>
          <div className="text-muted-foreground text-[11px]">Uptime, last 90 days</div>
        </div>
        <div className="text-right">
          <div className="text-foreground text-2xl font-semibold tracking-tight">2</div>
          <div className="text-muted-foreground text-[11px]">Minor incidents</div>
        </div>
      </div>
      {/* Live source — the numbers above come straight from our public status page */}
      <Link
        href={STATUS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="text-muted-foreground hover:text-foreground border-border mt-4 flex items-center justify-center gap-1.5 border-t pt-3 text-[11px] transition-colors"
      >
        Independently monitored by BetterStack
        <ArrowUpRight size={12} aria-hidden="true" />
      </Link>
    </div>
  );
}
